import React, { useState, useEffect } from 'react';
import { db, auth, functions } from '../lib/firebase';
import { doc, collection, runTransaction, onSnapshot, addDoc } from 'firebase/firestore';
import { useSystemConfig } from '../lib/config';
import { httpsCallable } from 'firebase/functions';
import { notify } from '../lib/toast';
import { Listing, User, CardInput, PaymentInitResponse, PaymentType } from '../types';
import { calculateDopingPrice, calculateRiskFreePrice, DOPING_CONFIG } from '../lib/payment/doping';
import PaymentMethodSelector from './payment/PaymentMethodSelector';
import CardForm from './payment/CardForm';
import ThreeDSecureModal from './payment/ThreeDSecureModal';

interface BoostModalProps {
    listing: Listing;
    user?: User;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const BoostModal: React.FC<BoostModalProps> = ({ listing, user, isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'DIRECT' | 'COMMISSION'>('DIRECT');
    const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'card'>('wallet');
    const [selectedDays, setSelectedDays] = useState<1 | 3 | 7>(7);
    const [hasConsented, setHasConsented] = useState(false);
    const [userBalance, setUserBalance] = useState(0);
    const [threeDSData, setThreeDSData] = useState<{ html: string; paymentId: string } | null>(null);
    const { config } = useSystemConfig();

    const standardPrice = calculateDopingPrice(listing.price, selectedDays, config?.dopingRates);
    const riskFreeRate = config?.boostStats?.riskFreeRate || 0.20;
    const riskFreePrice = calculateRiskFreePrice(standardPrice, riskFreeRate);
    const currentPrice = activeTab === 'COMMISSION' ? riskFreePrice : standardPrice;

    // Sync Balance
    useEffect(() => {
        if (!auth.currentUser || !isOpen) return;
        const balRef = doc(db, 'balances', auth.currentUser.uid);
        return onSnapshot(balRef, (snap) => {
            if (snap.exists()) setUserBalance(snap.data().available || 0);
        });
    }, [isOpen]);

    // Handle 3D Result
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === '3DS_RESULT') {
                setThreeDSData(null);
                setLoading(false);
                if (event.data.success) {
                    notify.success("Doping ödemesi başarılı! 🚀");
                    onSuccess();
                    onClose();
                } else {
                    notify.error("3D Secure doğrulaması başarısız.");
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onSuccess, onClose]);

    if (!isOpen) return null;

    const handleBoost = async () => {
        if (!auth.currentUser) return notify.error("Lütfen giriş yapın.");
        if (!hasConsented) return notify.error("Lütfen bilgilendirmeyi onaylayın.");

        setLoading(true);

        try {
            if (activeTab === 'COMMISSION') {
                // Risk-Free (Commission) Flow: Send to admin approval
                await addDoc(collection(db, 'pending_boosts'), {
                    listingId: listing.id,
                    userId: auth.currentUser.uid,
                    type: 'COMMISSION',
                    duration: selectedDays,
                    price: currentPrice,
                    listingTitle: listing.title,
                    marketRegion: (listing as any).marketRegion || 'TR',
                    createdAt: Date.now()
                });
                notify.success("Doping talebi yönetici onayına gönderildi. ⏳");
                onSuccess();
                onClose();
            } else if (paymentMethod === 'wallet') {
                // Wallet Flow
                const dopingWalletPay = httpsCallable<any, { success: boolean }>(functions, 'dopingWalletPay');
                const { data } = await dopingWalletPay({
                    listingId: listing.id,
                    package: selectedDays,
                    price: currentPrice
                });

                if (data.success) {
                    notify.success("Cüzdandan ödeme başarılı! Doping aktif edildi. 🚀");
                    onSuccess();
                    onClose();
                }
            }
        } catch (error: any) {
            console.error("Boost Error:", error);
            notify.error(error.message || "İşlem başarısız.");
        } finally {
            if (paymentMethod === 'wallet' || activeTab === 'COMMISSION') setLoading(false);
        }
    };

    const handleCardPayment = async (cardData: CardInput) => {
        if (!auth.currentUser || !listing) return;
        setLoading(true);

        try {
            const initPayment = httpsCallable<any, PaymentInitResponse>(functions, 'initPayment');
            const { data } = await initPayment({
                amount: currentPrice,
                type: PaymentType.BOOST_PAYMENT,
                card: cardData,
                listingId: listing.id,
                dopingPackage: selectedDays,
                idempotencyKey: `boost_${listing.id}_${Date.now()}`
            });

            if (data.requires3D && data.threeDSHtml) {
                setThreeDSData({ html: data.threeDSHtml, paymentId: data.paymentId });
            } else if (data.success) {
                notify.success('Ödeme başarılı! Doping aktif edildi. 🚀');
                onSuccess();
                onClose();
            } else {
                notify.error(data.errorMessage || 'Ödeme başarısız.');
            }
        } catch (error: any) {
            console.error("Payment Error:", error);
            notify.error(error.message || 'Ödeme hatası.');
        } finally {
            if (!threeDSData) setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full max-h-[90vh] overflow-y-auto shadow-2xl relative border border-brand-clay/10 animate-in fade-in zoom-in duration-200 custom-scrollbar">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors z-50 bg-white/80 backdrop-blur-md"
                >
                    ✕
                </button>

                <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-brand-terracotta/10 rounded-full flex items-center justify-center mx-auto text-brand-terracotta text-2xl mb-2">🚀</div>
                    <h2 className="text-xl font-black text-brand-clay uppercase tracking-tight">Doping Paketleri</h2>
                    <p className="text-xs text-gray-500 mt-1">Ürününüzü binlerce kişiye öne çıkarın.</p>
                </div>

                <div className="flex bg-gray-100 p-1 rounded-xl mb-6">
                    <button
                        onClick={() => setActiveTab('DIRECT')}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'DIRECT' ? 'bg-white text-brand-clay shadow-sm' : 'text-gray-500'}`}
                    >
                        Hemen Öde
                    </button>
                    <button
                        onClick={() => setActiveTab('COMMISSION')}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'COMMISSION' ? 'bg-white text-brand-terracotta shadow-sm' : 'text-gray-500'}`}
                    >
                        Risk Yok
                    </button>
                </div>

                <div className="space-y-3 mb-6">
                    {(Object.keys(DOPING_CONFIG.packages) as unknown as (1 | 3 | 7)[]).map((days) => {
                        const pkg = DOPING_CONFIG.packages[days];
                        const stdPrice = calculateDopingPrice(listing.price, days, config?.dopingRates);
                        const rfPrice = calculateRiskFreePrice(stdPrice, riskFreeRate);
                        const displayedPrice = activeTab === 'COMMISSION' ? rfPrice : stdPrice;
                        return (
                            <button
                                key={days}
                                onClick={() => setSelectedDays(days)}
                                className={`w-full p-4 border-2 rounded-xl flex justify-between items-center transition-all ${selectedDays === days ? 'border-brand-terracotta bg-brand-terracotta text-white shadow-lg scale-[1.02]' : 'border-transparent bg-gray-50 text-gray-600 hover:bg-orange-50'}`}
                            >
                                <div className="text-left">
                                    <p className="font-bold">{pkg.label}</p>
                                    <p className={`text-[9px] ${selectedDays === days ? 'text-white/70' : 'text-gray-400'}`}>Vitrin Garantisi</p>
                                </div>
                                <span className="font-black text-lg">₺{displayedPrice}</span>
                            </button>
                        );
                    })}
                </div>

                {activeTab === 'DIRECT' && (
                    <div className="mb-6 animate-in slide-in-from-bottom-4">
                        <PaymentMethodSelector
                            selectedMethod={paymentMethod}
                            onMethodSelect={setPaymentMethod}
                            price={currentPrice}
                            userBalance={userBalance}
                        />
                    </div>
                )}

                <div className={`p-4 rounded-2xl mb-4 text-[11px] leading-relaxed ${activeTab === 'DIRECT' ? 'bg-blue-50 text-blue-800' : 'bg-green-50 text-green-800'}`}>
                    <p className="font-bold uppercase mb-1">{activeTab === 'DIRECT' ? 'Peşin Ödeme' : 'Satıştan Kesilsin'}</p>
                    {activeTab === 'DIRECT'
                        ? "Ücret cüzdanınızdan anında düşülür. Ürün satılsa da satılmasa da vitrinde kalır."
                        : `Ücret ürün satıldığında satış tutarından ${currentPrice} TL olarak otomatik kesilir. Satış olmazsa ücret ödenmez.`
                    }
                </div>

                <div className="mb-6">
                    <label className="flex gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={hasConsented}
                            onChange={(e) => setHasConsented(e.target.checked)}
                            className="mt-1 accent-brand-terracotta"
                        />
                        <span className="text-[10px] text-gray-500 leading-tight">
                            {activeTab === 'DIRECT'
                                ? "Dopingin anında etkinleşeceğini, iade/iptal edilemeyeceğini kabul ediyorum."
                                : "Doping ücretinin satış olması durumunda komisyonuma ekleneceğini, satış olmazsa ücret ödemeyeceğimi kabul ediyorum."
                            }
                        </span>
                    </label>
                </div>

                {activeTab === 'DIRECT' && paymentMethod === 'card' ? (
                    <div className="animate-in fade-in zoom-in">
                        <CardForm
                            isProcessing={loading}
                            onSubmit={handleCardPayment}
                            submitLabel={`₺${currentPrice} ÖDE & BAŞLAT`}
                        />
                    </div>
                ) : (
                    <button
                        onClick={handleBoost}
                        disabled={loading || !hasConsented || (paymentMethod === 'wallet' && userBalance < currentPrice && activeTab === 'DIRECT')}
                        className={`w-full py-4 rounded-xl font-black uppercase tracking-widest transition-all ${activeTab === 'DIRECT' ? 'bg-brand-clay text-white' : 'bg-green-600 text-white shadow-lg shadow-green-600/20'}`}
                    >
                        {loading ? '...' : (activeTab === 'DIRECT' ? `ŞİMDİ ÖDE (₺${currentPrice})` : 'ONAYLA & BAŞLAT')}
                    </button>
                )}

                {threeDSData && (
                    <ThreeDSecureModal
                        htmlContent={threeDSData.html}
                        onClose={() => {
                            setThreeDSData(null);
                            setLoading(false);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default BoostModal;
